import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeMutation, createOrder, executeQuery } from '../../graphql';
import { getMiscVariantById } from '../../graphql/queries';
import Wizard from '../../components/Wizard';
import Tooltip from '../../components/Tooltip';
import { usePageContent } from '../../hooks/usePageContent';
import {
  GIFTVOUCHER_ORDER_TYPE,
  VPLATES_CHANNEL_ID,
  ORDER_NUMER_FORMAT,
  ORDER_NUMER_TYPE,
} from "../../constants/stringVariables";
import {
  giftItemFromOrder,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPayment,
} from '../../utils/analytics';
import './index.module.css';

const GiftCardPaymentPage = () => {
  const navigate = useNavigate();
  const { find } = usePageContent('gift-payment');
  const wizardContent = find('wizard') || {};
  const c  = find('paymentForm') || {};
  const ce = c.errors || {};
  const wizardSteps = wizardContent.steps || [
    { title: 'Personalise voucher',       href: '/gift-vouchers/personalise-voucher' },
    { title: 'Confirm delivery details',  href: '/gift-vouchers/confirm-delivery' },
    { title: 'Checkout',                  href: '/gift-vouchers/payment' },
  ];

  const publishableApiKey    = import.meta.env.VITE_QUICKSTREAM_PUBLISHABLE_KEY;
  const supplierBusinessCode = import.meta.env.VITE_QUICKSTREAM_SUPPLIER_CODE;

  const [giftData,            setGiftData]            = useState(null);
  const [variantAmount,       setVariantAmount]        = useState(0);
  const [loading,             setLoading]              = useState(true);
  const [trustedFrame,        setTrustedFrame]         = useState(null);
  const [paymentProcessing,   setPaymentProcessing]    = useState(false);
  const [surchargePercentage, setSurchargePercentage]  = useState(parseFloat(import.meta.env.VITE_QUICKSTREAM_SURCHARGE_PERCENTAGE) || 0);
  const [error,               setError]                = useState(null);
  const [attemptsMade,        setAttemptsMade]         = useState(0);
  const [termsAccepted,       setTermsAccepted]        = useState(false);
  const maxAttempts = 3;

  const cardSurcharge  = (variantAmount * surchargePercentage) / 100;
  const totalDue       = variantAmount + cardSurcharge;

  // GA: add_shipping_info fires once per mount (StrictMode runs this effect twice in dev).
  const shippingTrackedRef = useRef(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('giftOrderData');
    if (!stored) {
      navigate('/gift-vouchers/personalise-voucher');
      return;
    }
    const data = JSON.parse(stored);
    setGiftData(data);
    setVariantAmount(parseFloat(data.amount) || 0);
    setLoading(false);
    const trackOnce = (amount) => {
      if (shippingTrackedRef.current) return;
      shippingTrackedRef.current = true;
      trackAddShippingInfo(giftItemFromOrder(data, amount), amount);
    };
    if (data.variantId) {
      executeQuery(getMiscVariantById, { id: data.variantId })
        .then(result => {
          const cost = result?.getMiscVariantById?.CostInfo?.Cost;
          if (cost != null) setVariantAmount(parseFloat(cost) || 0);
          trackOnce(cost ?? data.amount);
        })
        .catch(() => { trackOnce(data.amount); });
    } else {
      trackOnce(data.amount);
    }
  }, [navigate]);

  const initializeQuickStream = useCallback(() => {
    if (!window.QuickstreamAPI) {
      setError('Payment system is not available. Please refresh the page.');
      return;
    }
    try {
      window.QuickstreamAPI.init({ publishableApiKey });

      const inputStyle = {
        height: 'auto', border: '0', background: 'none', 'border-radius': '0',
        color: '#333', padding: '14px 0 10px', 'line-height': '1',
        'font-size': '16px', 'border-bottom': '2px solid #d2d1d2',
      };

      const options = {
        config: { supplierBusinessCode, surchargePercentage },
        iframe: { width: '100%', height: window.innerWidth < 1440 ? '450px' : '430px' },
        body: { style: { background: 'none' } },
        labels: { style: { color: '#231F20', 'font-weight': '700', 'line-height': '1.5', 'font-size': '16px' } },
        cardholderName:  { label: 'Card holder name',           placeholder: 'E.g. Graham Smith',        style: inputStyle },
        cardNumber:      { label: 'Credit card number',         placeholder: 'E.g. 1234 1234 1234 1234', style: inputStyle },
        expiryDateMonth: { label: 'Expiry date',                                                          style: inputStyle },
        expiryDateYear:  { label: 'Expiry year',                                                          style: inputStyle },
        cvn:             { label: 'CVV number', placeholder: 'E.g. 388', style: { ...inputStyle, width: '84px' } },
      };

      window.QuickstreamAPI.creditCards.createTrustedFrame(options, (errors, data) => {
        if (errors) {
          const msg = Array.isArray(errors)
            ? errors.map(e => e.message || e).join('. ')
            : 'Failed to initialize payment system';
          setError(msg);
        } else {
          setTrustedFrame(data.trustedFrame);
        }
      });
    } catch {
      setError('Failed to initialize payment system. Please refresh the page.');
    }
  }, [publishableApiKey, supplierBusinessCode]);

  useEffect(() => {
    if (!giftData) return;
    const existingScript = document.getElementById('quickstream-script');
    if (existingScript) {
      if (window.QuickstreamAPI) initializeQuickStream();
      return;
    }
    const script = document.createElement('script');
    script.id    = 'quickstream-script';
    script.src   = `${import.meta.env.VITE_QUICKSTREAM_BASE_URL}quickstream-api-1.0.min.js`;
    script.async = true;
    script.onload  = () => initializeQuickStream();
    script.onerror = () => setError('Failed to load payment system. Please refresh the page.');
    document.body.appendChild(script);
    return () => {
      const el = document.getElementById('quickstream-script');
      if (el) document.body.removeChild(el);
    };
  }, [giftData, initializeQuickStream]);

  const handlePlaceOrder = () => {
    if (!trustedFrame) {
      setError('Payment system not initialized. Please refresh the page.');
      return;
    }
    // GA: add_payment_info on PLACE ORDER click, before card verification (prod: orderPayment.js).
    if (giftData) trackAddPaymentInfo(giftItemFromOrder(giftData, variantAmount), variantAmount);
    setPaymentProcessing(true);
    setError(null);

    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      setPaymentProcessing(false);
      setError(ce.cardVerifyTimeout || 'Card verification timed out. Please try again.');
    }, 30000);

    trustedFrame.submitForm(async (errors, data) => {
      if (timedOut) return;
      clearTimeout(timeoutId);

      if (errors) {
        setPaymentProcessing(false);
        const fieldLabels = {
          cardholderName: 'Card holder name',
          cardNumber: 'Credit card number',
          expiryDateMonth: 'Expiry date',
          expiryDateYear: 'Expiry year',
          cvn: 'CVV number',
        };
        const isValidationError = Array.isArray(errors) && errors.some((e) => e?.fieldName);
        if (isValidationError) {
          const messages = errors.flatMap((e) => {
            const label = fieldLabels[e?.fieldName] || '';
            if (Array.isArray(e?.messages)) return e.messages.map((m) => ({ label, message: m }));
            if (typeof e?.message === 'string') return [{ label, message: e.message }];
            if (typeof e === 'string') return [{ label: '', message: e }];
            return [{ label: '', message: ce.cardVerifyFailed || 'Card verification failed.' }];
          });
          setError(messages);
        } else {
          setAttemptsMade(prev => {
            const next = prev + 1;
            if (next >= maxAttempts) {
              setError(ce.tooManyAttempts || 'Too many failed attempts. Please check your card details or try a different card.');
            } else {
              const messages = Array.isArray(errors)
                ? errors.flatMap((e) => {
                    const label = fieldLabels[e?.fieldName] || '';
                    if (Array.isArray(e?.messages)) return e.messages.map((m) => ({ label, message: m }));
                    if (typeof e?.message === 'string') return [{ label, message: e.message }];
                    if (typeof e === 'string') return [{ label: '', message: e }];
                    return [{ label: '', message: ce.cardVerifyFailed || 'Card verification failed.' }];
                  })
                : [{ label: '', message: ce.cardVerifyFailed || 'Card verification failed. Please check your details and try again.' }];
              setError(messages);
            }
            return next;
          });
        }
      } else {
        const token = data.singleUseToken.singleUseTokenId;
        setSurchargePercentage(data.singleUseToken.creditCard.surchargePercentage || 0);
        sessionStorage.setItem('giftCardDetails', JSON.stringify(data.singleUseToken.creditCard));
        try {
          const result      = await submitGiftOrder(token);
          const orderNumber = result?.takePaymentAndPlaceOrder?.OrderNumber;
          sessionStorage.setItem('orderNumber', orderNumber || '');
          sessionStorage.setItem('giftPaymentReceipt', JSON.stringify({ orderNumber }));
          trackPayment(giftItemFromOrder(giftData, variantAmount), variantAmount, orderNumber);
          navigate('/gift-vouchers/order-confirmation');
        } catch (err) {
          const errMsg = Array.isArray(err) ? err[0]?.message : (err?.message || 'Please try again or contact support.');
          let msg = ce.paymentFailedPrefix || 'Payment processing failed. ';
          if (errMsg.includes('declined'))     msg += ce.cardDeclined   || 'Your card was declined. Please check your card details or try a different card.';
          else if (errMsg.includes('timeout')) msg += ce.paymentTimeout || 'The payment request timed out. Please try again.';
          else msg += errMsg;
          setError(msg);
          setPaymentProcessing(false);
        }
      }
    });
  };

  const submitGiftOrder = async (token) => {
    const nameParts  = (giftData.sender?.fullName || '').trim().split(/\s+/);
    const firstName  = nameParts[0] || '';
    const lastName   = nameParts.slice(1).join(' ') || firstName;

    return await executeMutation(createOrder, {
      input: {
        OrderNumberType : ORDER_NUMER_TYPE,
        SingleUseToken  : token,
        OrderNumberFormat: ORDER_NUMER_FORMAT,
        OrderType       : GIFTVOUCHER_ORDER_TYPE,
        PlateCombo      : '',
        ClientId        : '',
        ChannelId       : VPLATES_CHANNEL_ID,
        FirstName       : firstName,
        LastName        : lastName,
        Email           : giftData.sender?.email || '',
        Mobile          : giftData.sender?.phone || '',
        OfficeId        : '0960',
        VRISId          : 0,
        CPMDemandId     : '',
        TierProductId   : 'Perpetual',
        VariantId       : giftData.variantId || 0,
        VehicleTypeId   : 1,
        StylePrice      : 0,
        IncludesPlates  : false,
        RecipientEmail  : giftData.sendTo === 'recipient' ? (giftData.recipient?.email || '') : (giftData.sender?.email || ''),
        RecipientName   : giftData.sendTo === 'recipient' ? (giftData.recipient?.fullName || '') : (giftData.sender?.fullName || ''),
        SenderName      : giftData.sender?.fullName || '',
        CreatedBy       : firstName,
        GVTo            : giftData.to || '',
        GVFrom          : giftData.from || '',
        GVMessage       : giftData.message || ''
      },
    });
  };

  if (loading || !giftData) {
    return (
      <div id="content">
        <div className="row">
          <div className="plate-wizard">
            <div className="loading__panel" role="alert">{c.loadingText || 'Loading...'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="content">
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Gift vouchers'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={2}
            backLink="/gift-vouchers/confirm-delivery"
            backLabel={c.backLabel || 'Back to delivery details'}
          />

          <div className="plate-wizard__form plate-wizard__form--payment">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">{c.sectionTitle || 'Checkout'}</h2>
            </div>

            <div>
              <div className="order-payment">
                <p className="order-payment__accessible-loading visuallyhidden" role="alert"></p>

                {error && !Array.isArray(error) && (
                  <div className="error-bar error-bar--component-prep-error" role="alert" tabIndex="-1">
                    {(Array.isArray(error) ? error : [error]).map((msg, i) => (
                      <p key={i}>{typeof msg === 'object' && msg.label ? <><strong>{msg.label}:</strong> {msg.message}</> : (msg?.message || msg)}</p>
                    ))}
                  </div>
                )}

                <div className="order-payment__inner">
                  <div className="order-payment__order">
                    <h3 className="visuallyhidden">{c.orderSummaryTitle || 'Your gift voucher order'}</h3>
                    <dl className="order-payment__order-list">
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.voucherAmountKey || 'Voucher amount'}</dt>
                        <dd className="order-payment__order-value">${parseFloat(variantAmount).toFixed(2)}</dd>
                      </div>
                      {giftData.to && (
                        <div className="order-payment__order-item">
                          <dt className="order-payment__order-key">{c.toKey || 'To'}</dt>
                          <dd className="order-payment__order-value">{giftData.to}</dd>
                        </div>
                      )}
                      {giftData.from && (
                        <div className="order-payment__order-item">
                          <dt className="order-payment__order-key">{c.fromKey || 'From'}</dt>
                          <dd className="order-payment__order-value">{giftData.from}</dd>
                        </div>
                      )}
                      {giftData.message && (
                        <div className="order-payment__order-item">
                          <dt className="order-payment__order-key">{c.messageKey || 'Message'}</dt>
                          <dd className="order-payment__order-value">{giftData.message}</dd>
                        </div>
                      )}
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.senderKey || 'Sender'}</dt>
                        <dd className="order-payment__order-value">{giftData.sender?.fullName}</dd>
                      </div>
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.deliveryKey || 'Delivery'}</dt>
                        <dd className="order-payment__order-value">
                          {giftData.sendTo === 'recipient'
                            ? `${c.deliveryToRecipientPrefix || 'To recipient:'} ${giftData.recipient?.fullName} (${giftData.recipient?.email})`
                            : `${c.deliveryToMyEmailPrefix || 'To my email:'} ${giftData.sender?.email}`}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <form className="order-payment__form" onSubmit={e => e.preventDefault()}>
                    <div className="order-payment__entry">
                      <h3 className="order-payment__entry-title">{c.paymentDetailsTitle || 'Payment details'}</h3>

                      {error && (
                        <div className="order-payment__form-errors" role="alert" tabIndex="-1">
                          <div className="order-payment__form-errors-inner">
                            <h4 className="visuallyhidden">There are errors in this form</h4>
                            <p className="order-payment__form-errors-intro">{c.formErrorsIntro || 'Please review the following areas before submitting:'}</p>
                            <ul className="order-payment__form-errors-list">
                              {(Array.isArray(error) ? error : [error]).map((msg, i) => (
                                <li key={i} className="order-payment__form-errors-item">
                                  <span>{typeof msg === 'object' && msg.label ? <><span className="order-payment__form-errors-label">{msg.label}:</span> {msg.message}</> : (msg?.message || msg)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      <div id="trusted-frame-container">
                        <div data-quickstream-api="creditCardContainer"></div>
                      </div>
                    </div>

                    {/* Payment summary — always visible once gift data is loaded */}
                    <div className="order-payment__summary">
                      <h3 className="order-payment__summary-title">{c.summaryTitle || 'Payment summary'}</h3>
                      <dl className="order-payment__summary-list">
                        <div className="order-payment__summary-list-item">
                          <dt className="order-payment__summary-list-key">{c.voucherAmountSummaryKey || 'Voucher amount'}</dt>
                          <dd className="order-payment__summary-list-value">${parseFloat(variantAmount).toFixed(2)} AUD</dd>
                        </div>
                        {surchargePercentage > 0 && (
                          <div className="order-payment__summary-list-item">
                            <dt className="order-payment__summary-list-key">
                              {c.cardFeeKey || 'Card payment fee'}
                              {c.feeTooltipHtml && <Tooltip content={<span dangerouslySetInnerHTML={{ __html: c.feeTooltipHtml }} />} type="question" />}
                            </dt>
                            <dd className="order-payment__summary-list-value">${cardSurcharge.toFixed(2)}</dd>
                          </div>
                        )}
                        <div className="order-payment__summary-list-item order-payment__summary-list-item--total">
                          <dt className="order-payment__summary-list-key">{c.totalDueKey || 'Total due'}</dt>
                          <dd className="order-payment__summary-list-value">${totalDue.toFixed(2)} AUD</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="order-payment__form-field">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        className="order-payment__form-checkbox-input visuallyhidden visuallyhidden--no-focus"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <label className="order-payment__form-checkbox-label" htmlFor="terms-checkbox">
                        {c.termsLabelBefore || 'I agree to the'}{' '}
                        <a href={c.termsLabelLink || '/terms'} className="order-payment__form-checkbox-label-link">
                          {c.termsLabelLinkLabel || 'Terms and Conditions'}
                        </a>{' '}
                        {c.termsLabelAfter || 'of sale'}
                      </label>
                    </div>

                    <div className="order-payment__form-actions">
                      <button
                        type="button"
                        className={`order-payment__form-cta${(paymentProcessing || !termsAccepted) ? ' order-payment__form-cta--disabled' : ''}`}
                        disabled={paymentProcessing || !termsAccepted}
                        onClick={handlePlaceOrder}
                      >
                        {paymentProcessing ? (c.processingText || 'Processing...') : (c.placeOrderText || 'Place order')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardPaymentPage;
